// How recently a school's subscription must have lapsed for the churn pitch to be honest.
// Past this, "your subscription has expired" is stale news, so the modals fall back to the
// standing offer's plain copy. Mirrors CHURN_WINDOW_MONTHS in diffit-api's
// share_with_admin_controller, which gates the same wording on the email it sends.
export const CHURN_WINDOW_MONTHS = 4;

// The backend serializes Org.subscription_end_date from a naive datetime compared against
// utcnow(), so its ISO string carries no offset — and JS reads an offset-less date-time as
// LOCAL. Left alone, client and server disagree by one timezone offset around both the expiry
// instant and the 4-month cutoff, which is exactly where the modal's copy and the backend's
// grant stop matching.
const parseUtc = (value) => {
  const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`;
  return new Date(normalized).getTime();
};

const DAY_MS = 86400000;

// Fallback when the `expiring-soon-window-days` LaunchDarkly flag is unset or malformed.
// diffit-api reads the same flag, so both sides agree on the window.
export const EXPIRING_SOON_WINDOW_DAYS = 3;

/** The flag's value when it's a valid window, else EXPIRING_SOON_WINDOW_DAYS. */
export const resolveExpiringSoonWindowDays = (flagValue) =>
  Number.isInteger(flagValue) && flagValue > 0 ? flagValue : EXPIRING_SOON_WINDOW_DAYS;

/**
 * Whole days until this teacher's school access actually stops, or null when nothing is on
 * the clock.
 *
 * Reads the LATEST end date still in the future, not the soonest: a teacher who belongs to two
 * orgs keeps premium until the last one lapses, so counting down to the first would warn them
 * about an expiry they will never feel.
 *
 * Rounds up and floors at 1 — a subscription that has not ended yet must never render as
 * "ends in 0 days", and once it has ended this returns null and the lapsed copy takes over.
 */
export const getDaysUntilSubscriptionEnds = (diffitUser) => {
  const now = Date.now();
  let latest = null;
  for (const orgUserInfo of diffitUser?.org_user_infos || []) {
    const endsAt = orgUserInfo?.org?.subscription_end_date_iso;
    if (!endsAt) continue;
    const endsAtMs = parseUtc(endsAt);
    // Same NaN reasoning as getChurnState: an unparseable date is "unknown", not "expiring".
    if (Number.isNaN(endsAtMs) || endsAtMs <= now) continue;
    if (latest === null || endsAtMs > latest) latest = endsAtMs;
  }
  if (latest === null) return null;
  return Math.max(1, Math.ceil((latest - now) / DAY_MS));
};

const monthsAgo = (months, from = new Date()) => {
  const d = new Date(from);
  d.setMonth(d.getMonth() - months);
  return d.getTime();
};

/**
 * How a teacher's school subscription stands: `{ state, org }`.
 *
 * `state` is 'recent' (lapsed inside the window — show the renewal copy), 'stale' (lapsed
 * longer ago) or 'none'. `org` is the most recently expired org_user_info, or null.
 *
 * "Churned" is deliberately narrower than "not premium": the teacher must belong to an org
 * that once had a subscription and no longer does. An org with no `subscription_end_date`
 * never paid, so it hasn't expired — that teacher is just on the free tier and telling them
 * their subscription lapsed would be wrong.
 *
 * Any active org disqualifies them: someone who kept one school's license hasn't lost access,
 * whatever a second org's dates say.
 */
export const getChurnState = (diffitUser) => {
  const orgs = diffitUser?.org_user_infos || [];
  const now = Date.now();
  const cutoff = monthsAgo(CHURN_WINDOW_MONTHS);

  const ended = [];
  for (const orgUserInfo of orgs) {
    const endsAt = orgUserInfo?.org?.subscription_end_date_iso;
    if (!endsAt) continue;
    const endsAtMs = parseUtc(endsAt);
    // A malformed date reads as NaN, and every comparison against it is false — so it
    // neither counts as expired nor as active, which is the right answer for "unknown".
    if (Number.isNaN(endsAtMs)) continue;
    if (endsAtMs > now) return { state: 'none', org: null };
    ended.push({ orgUserInfo, endsAtMs });
  }

  if (ended.length === 0) return { state: 'none', org: null };

  // The most recently expired is the subscription the teacher actually remembers having.
  const latest = ended.reduce((a, b) => (b.endsAtMs > a.endsAtMs ? b : a));
  return {
    state: latest.endsAtMs >= cutoff ? 'recent' : 'stale',
    org: latest.orgUserInfo,
  };
};

/** The org whose subscription a teacher recently lost, or null. */
export const getChurnedOrg = (diffitUser) => {
  const { state, org } = getChurnState(diffitUser);
  return state === 'recent' ? org : null;
};

/** The recently-churned org's display name, or null when there isn't one to name. */
export const getChurnedOrgName = (diffitUser) => getChurnedOrg(diffitUser)?.org?.name || null;

/**
 * Whole days until this teacher's own free trial runs out, or null when no trial is running.
 *
 * Reads `trial_end_date_iso` rather than the `days_left_in_trial` the backend also sends: that
 * one floors (`(end - utcnow).days`) and is computed when the profile was fetched, so a trial
 * with 6h left reports 0 days. Rounding up off the raw date matches the org path above, which
 * is what lets one countdown read from either clock without changing its wording.
 */
export const getDaysUntilTrialEnds = (diffitUser) => {
  const endsAt = diffitUser?.trial_end_date_iso;
  // Absent only when the profile hasn't loaded: the backend never sends null, it substitutes
  // GRANDFATHER_TRIAL_END_DATE (2024-03-01) for an account with no trial of its own.
  if (!endsAt) return null;
  const endsAtMs = parseUtc(endsAt);
  const now = Date.now();
  if (Number.isNaN(endsAtMs) || endsAtMs <= now) return null;
  return Math.max(1, Math.ceil((endsAtMs - now) / DAY_MS));
};

/**
 * Whole days until this teacher's premium actually stops, whichever clock is running it: the
 * school licence when they have one, otherwise their own trial.
 *
 * The school licence wins when both are running — it is the one that survives the other, and
 * it is the one the renewal ask is about.
 */
export const getDaysUntilPremiumEnds = (diffitUser) =>
  getDaysUntilSubscriptionEnds(diffitUser) ?? getDaysUntilTrialEnds(diffitUser);

/**
 * Whether this teacher's school licence is close enough to lapsing to warn them about it.
 *
 * Requires the org licence to be the ONLY thing making them premium. Someone who also holds an
 * individual plan, sits on a premium domain, or is an ambassador keeps premium when the org
 * lapses, so counting their school's subscription down would be warning them about nothing.
 *
 * `windowDays` is the caller's resolved `expiring-soon-window-days` flag value (see
 * resolveExpiringSoonWindowDays) — required so this can't silently fall back to a stale window.
 */
export const isPremiumExpiringSoon = (diffitUser, windowDays) => {
  if (!diffitUser?.is_in_premium_org) return false;
  if (
    diffitUser.is_active_individual_subscription ||
    diffitUser.is_in_premium_domain ||
    diffitUser.is_ambassador
  ) {
    return false;
  }
  const daysLeft = getDaysUntilSubscriptionEnds(diffitUser);
  return daysLeft !== null && daysLeft <= windowDays;
};

/**
 * Whether this teacher's own trial is close enough to running out to warn them — and nothing
 * else is carrying their premium, so its end really is when they lose it.
 *
 * The counterpart to isPremiumExpiringSoon for a teacher whose school never subscribed. Unlike
 * that one, the 30-day extension IS on the table here: the promo code refuses a paid user, and
 * this teacher is not one — their trial is simply pushed out.
 *
 * `windowDays` — see isPremiumExpiringSoon.
 */
export const isTrialExpiringSoon = (diffitUser, windowDays) => {
  if (
    !diffitUser ||
    diffitUser.is_in_premium_org ||
    diffitUser.is_active_individual_subscription ||
    diffitUser.is_in_premium_domain ||
    diffitUser.is_ambassador
  ) {
    return false;
  }
  const daysLeft = getDaysUntilTrialEnds(diffitUser);
  return daysLeft !== null && daysLeft <= windowDays;
};

/**
 * Whether this teacher's own trial ran out recently, with nothing else premium since.
 *
 * The lapsed counterpart to isTrialExpiringSoon, and windowed for the same reason getChurnState
 * is: "your trial has ended" is news for a few months and stale after that, at which point they
 * are simply a free-tier teacher. Shares CHURN_WINDOW_MONTHS deliberately — one answer to "did
 * they lose premium recently enough to say so", whichever clock it was.
 */
export const isTrialRecentlyEnded = (diffitUser) => {
  if (
    !diffitUser ||
    diffitUser.is_in_premium_org ||
    diffitUser.is_active_individual_subscription ||
    diffitUser.is_in_premium_domain ||
    diffitUser.is_ambassador
  ) {
    return false;
  }
  const endsAt = diffitUser.trial_end_date_iso;
  if (!endsAt) return false;
  const endsAtMs = parseUtc(endsAt);
  // Still running is not ended, and an unparseable date is "unknown" rather than either.
  if (Number.isNaN(endsAtMs) || endsAtMs > Date.now()) return false;
  // The window is also what keeps grandfathered accounts out: they report 2024-03-01, which
  // only reads as "long ago" because that constant is — the backend checks the raw column.
  return endsAtMs >= monthsAgo(CHURN_WINDOW_MONTHS);
};

/**
 * Whether this teacher is in the expiry window: premium about to end, or ended recently enough
 * to say so. The expiry ads and the paygates' admin ask are both limited to it.
 *
 * `windowDays` — see isPremiumExpiringSoon.
 */
export const isInExpiryWindow = (diffitUser, windowDays) =>
  isPremiumExpiringSoon(diffitUser, windowDays) ||
  isTrialExpiringSoon(diffitUser, windowDays) ||
  isTrialRecentlyEnded(diffitUser) ||
  getChurnState(diffitUser).state === 'recent';
